import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraBadge, SpecraButton, SpecraCheckboxField, SpecraInput, SpecraTabBar, SpecraTabPanel } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useMemo, useState } from 'react';
import { EmptyState } from '../../../components/layout';
import { useDebounce } from '../../../hooks/useDebounce';
import { cn } from '../../../lib/utils';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { useOwnCompanyProfile } from '../Programs/referenceDataHooks';
import { BURDEN_LABELS, companyProfileHooks, contractVehicleHooks, laborCategoryHooks, RATE_TYPE_LABELS, useAcceptLaborCategoryCandidate, useApproveLaborCategoryMapping, useCorrectRateObservation, useCreateRateObservation, useDeleteRateObservation, useLaborCategoryAnchors, useLaborCategoryCandidates, useLaborCategoryHistory, useLaborCategoryMappings, useProposeLaborCategoryMapping, useRateObservations, useRejectLaborCategoryCandidate, useRejectLaborCategoryMapping } from './hooks';
// Human labels for the three edge types — curated, never transitive.
const MAPPING_TYPE_LABELS = {
    EQUIVALENT: 'Equivalent to',
    BROADER: 'Broader than',
    NARROWER: 'Narrower than'
};
const mappingTarget = (m)=>m.toAnchorId ? `${m.toAnchorSocCode} ${m.toAnchorTitle}` : m.toEntryTitle || m.toEntryId;
// Compact reviewer-facing summary of a mapping's evidence_json — importers
// and the zou feed attach real evidence; hand-proposed edges carry "{}".
const evidenceSummary = (json)=>{
    try {
        const obj = JSON.parse(json);
        if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) return '—';
        const parts = Object.entries(obj).map(([k, v])=>`${k}: ${String(v)}`);
        return parts.length ? parts.join(' · ') : '—';
    } catch  {
        return '—';
    }
};
const NONE = '__none__';
const lcatDefaults = (e)=>({
        id: e?.id ?? '',
        title: e?.title ?? '',
        description: e?.description ?? '',
        qualifications: e?.qualifications ?? '',
        clearance: e?.clearance ?? '',
        certifications: e?.certifications ?? '',
        companyProfileId: e?.companyProfileId || NONE,
        contractVehicleId: e?.contractVehicleId || NONE
    });
const entryColumns = [
    {
        key: 'title',
        header: 'Title'
    },
    {
        key: 'companyName',
        header: 'Company',
        render: (e)=>e.companyName || '—'
    },
    {
        key: 'contractVehicleName',
        header: 'Vehicle',
        render: (e)=>e.contractVehicleName || '—'
    },
    {
        key: 'clearance',
        header: 'Clearance',
        render: (e)=>e.clearance || '—'
    },
    {
        key: 'version',
        header: 'Version',
        render: (e)=>`v${e.version}`
    },
    {
        key: 'source',
        header: 'Source',
        render: (e)=>e.source === 'CANDIDATE' ? 'Extraction' : 'Manual'
    }
];
export function LaborCategoriesTab() {
    const tenantId = useTenantId();
    const [view, setView] = useState('catalog');
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    // Own-company lens (06-phase-2b-followups): filter the catalog to entries
    // attributed to the tenant's own org profile. Company-less (as-written)
    // rows drop out while the lens is on — that is the lens's meaning.
    const [oursOnly, setOursOnly] = useState(false);
    const ownProfile = useOwnCompanyProfile().data ?? null;
    const entriesQuery = laborCategoryHooks.useList({
        companyId: tenantId,
        search: debouncedSearch,
        companyProfileId: oursOnly && ownProfile ? ownProfile.id : undefined
    });
    const entries = entriesQuery.data?.items ?? [];
    // Unfiltered heads: the drawer derives its record here (so an open drawer
    // survives search/lens changes), the proposer's entry targets come from
    // here, and Compare resolves entry metadata here.
    const allEntriesQuery = laborCategoryHooks.useList({
        companyId: tenantId
    });
    const allEntries = allEntriesQuery.data?.items ?? [];
    const upsert = laborCategoryHooks.useUpsert();
    const remove = laborCategoryHooks.useDelete();
    const candidatesQuery = useLaborCategoryCandidates(tenantId, 'PENDING');
    const candidates = candidatesQuery.data ?? [];
    const accept = useAcceptLaborCategoryCandidate();
    const reject = useRejectLaborCategoryCandidate();
    // Rosetta: pending edges feed the review queue (and its toggle badge);
    // anchors feed the drawer's proposer and the Compare view.
    const pendingMappingsQuery = useLaborCategoryMappings(tenantId, {
        status: 'PENDING'
    });
    const pendingMappings = pendingMappingsQuery.data ?? [];
    const approveMapping = useApproveLaborCategoryMapping();
    const rejectMapping = useRejectLaborCategoryMapping();
    const anchorsQuery = useLaborCategoryAnchors(tenantId);
    const anchors = anchorsQuery.data ?? [];
    const propose = useProposeLaborCategoryMapping();
    // VEHICLE_MISMATCH (04-phase-2b-rosetta.md): a pending candidate whose
    // title matches existing entries that live on a vehicle is flagged —
    // accepting keeps it solicitation-scoped (empty vehicle) rather than
    // silently minting it into that vehicle's canonical catalog.
    const vehicleMismatch = (c)=>allEntries.some((e)=>e.title.toLowerCase() === c.title.toLowerCase() && e.contractVehicleId !== '' && e.contractVehicleId !== c.contractVehicleId);
    // Compare: pick an anchor, see every company's APPROVED-mapped entry side
    // by side — the Rosetta payoff view, pre-rates.
    const [compareAnchorId, setCompareAnchorId] = useState('');
    const compareQuery = useLaborCategoryMappings(tenantId, {
        toAnchorId: compareAnchorId,
        status: 'APPROVED'
    }, {
        enabled: view === 'compare' && !!compareAnchorId
    });
    const compareMappings = compareQuery.data ?? [];
    // Drawer Mappings section state: existing edges for the open entry + the
    // proposer. Target is EXACTLY ONE of a SOC anchor or another entry (the
    // dedup move) — mirrors the one-target CHECK on the table.
    const [mapTargetKind, setMapTargetKind] = useState('ANCHOR');
    const [mapAnchorId, setMapAnchorId] = useState('');
    const [mapEntryId, setMapEntryId] = useState('');
    const [mapType, setMapType] = useState('EQUIVALENT');
    // Company/vehicle pickers on the create/edit form — the shared catalogs.
    const companiesQuery = companyProfileHooks.useList({
        companyId: tenantId
    });
    const vehiclesQuery = contractVehicleHooks.useList({
        companyId: tenantId
    });
    const companyOptions = [
        {
            value: NONE,
            label: 'None (government / as-written)'
        },
        ...(companiesQuery.data?.items ?? []).map((c)=>({
                value: c.id,
                label: c.name
            }))
    ];
    const vehicleOptions = [
        {
            value: NONE,
            label: 'None'
        },
        ...(vehiclesQuery.data?.items ?? []).map((v)=>({
                value: v.id,
                label: v.name
            }))
    ];
    const fields = [
        {
            kind: 'text',
            name: 'title',
            label: 'Title (as written)',
            required: true,
            maxLength: 255
        },
        {
            kind: 'select',
            name: 'companyProfileId',
            label: 'Company',
            options: companyOptions
        },
        {
            kind: 'select',
            name: 'contractVehicleId',
            label: 'Vehicle',
            options: vehicleOptions
        },
        {
            kind: 'textarea',
            name: 'description',
            label: 'Description',
            rows: 3
        },
        {
            kind: 'textarea',
            name: 'qualifications',
            label: 'Qualifications',
            rows: 3
        },
        {
            kind: 'text',
            name: 'clearance',
            label: 'Clearance',
            maxLength: 100
        },
        {
            kind: 'textarea',
            name: 'certifications',
            label: 'Certifications',
            rows: 2
        }
    ];
    const [formOpen, setFormOpen] = useState(false);
    // Derive the drawer record from the UNFILTERED list by id — never sync into
    // state, and never let the search box or the own-company lens close an
    // already-open drawer.
    const [detailId, setDetailId] = useState(null);
    const detail = detailId ? allEntries.find((e)=>e.id === detailId) ?? null : null;
    // Reset the proposer whenever the drawer moves to a different entry: a
    // target picked for entry A must never ride along and end up proposed from
    // entry C's drawer — that queue row would look perfectly well-formed to
    // the reviewer (#997 review finding).
    useEffect(()=>{
        setMapTargetKind('ANCHOR');
        setMapAnchorId('');
        setMapEntryId('');
        setMapType('EQUIVALENT');
        setRateType('CEILING');
        setRateAmount('');
        setRatePeriod('');
        setRateBurden('FULLY_BURDENED');
        setCorrectingRateId(null);
        setCorrectionAmount('');
    }, [
        detailId
    ]);
    const [detailMode, setDetailMode] = useState('read');
    // Rate Book (Phase 4 slice 1). The drawer asks the LOGICAL question
    // (across_versions) because a rate pins the entry version that was current
    // when it was observed; Compare reads the whole book once and groups by
    // entry so each column can show its own typed rates.
    const drawerRatesQuery = useRateObservations(tenantId, {
        lcatEntryId: detailId ?? '',
        acrossVersions: true,
        includeCorrected: true
    }, {
        enabled: !!detailId
    });
    const drawerRates = drawerRatesQuery.data ?? [];
    // A row somebody else corrects is history. It stays on screen — that is the
    // whole point of append-only — but it reads as superseded and cannot be
    // corrected or removed again.
    const supersededRateIds = useMemo(()=>new Set(drawerRates.map((r)=>r.correctsId).filter(Boolean)), [
        drawerRates
    ]);
    // Server order is newest-first, which would strand a correction far from the
    // figure it replaced. Read order keeps that newest-first spine but pulls each
    // struck original up next to its correction, so the pair reads as one fact
    // changing rather than two unrelated rows.
    const orderedRates = useMemo(()=>{
        const byID = new Map(drawerRates.map((r)=>[
                r.id,
                r
            ]));
        const out = [];
        const placed = new Set();
        const push = (r)=>{
            if (!r || placed.has(r.id)) return;
            placed.add(r.id);
            out.push(r);
        };
        for (const r of drawerRates){
            if (r.correctsId) {
                push(byID.get(r.correctsId));
                push(r);
                continue;
            }
            push(r);
        }
        return out;
    }, [
        drawerRates
    ]);
    const createRate = useCreateRateObservation();
    const correctRate = useCorrectRateObservation();
    const deleteRate = useDeleteRateObservation();
    const [rateType, setRateType] = useState('CEILING');
    const [rateAmount, setRateAmount] = useState('');
    const [ratePeriod, setRatePeriod] = useState('');
    const [rateBurden, setRateBurden] = useState('FULLY_BURDENED');
    // Correcting is a SECOND observation that points at the first, so the inline
    // editor is its own little form — never an edit-in-place on the row.
    const [correctingRateId, setCorrectingRateId] = useState(null);
    const [correctionAmount, setCorrectionAmount] = useState('');
    const compareRatesQuery = useRateObservations(tenantId, {
        limit: 500
    }, {
        enabled: view === 'compare' && !!compareAnchorId
    });
    const ratesByEntry = useMemo(()=>{
        const m = new Map();
        for (const r of compareRatesQuery.data ?? []){
            const list = m.get(r.lcatEntryId) ?? [];
            list.push(r);
            m.set(r.lcatEntryId, list);
        }
        return m;
    }, [
        compareRatesQuery.data
    ]);
    const [deleting, setDeleting] = useState(null);
    const historyQuery = useLaborCategoryHistory(detailId, tenantId);
    const history = historyQuery.data ?? [];
    const entryMappingsQuery = useLaborCategoryMappings(tenantId, {
        fromEntryId: detailId ?? ''
    }, {
        enabled: !!detailId
    });
    const entryMappings = entryMappingsQuery.data ?? [];
    const submitEntry = async (values, id)=>{
        const saved = await upsert.mutateAsync({
            id,
            companyId: tenantId,
            companyProfileId: values.companyProfileId === NONE ? '' : values.companyProfileId ?? '',
            contractVehicleId: values.contractVehicleId === NONE ? '' : values.contractVehicleId ?? '',
            title: values.title,
            description: values.description ?? '',
            qualifications: values.qualifications ?? '',
            clearance: values.clearance ?? '',
            certifications: values.certifications ?? ''
        });
        // An edit mints a NEW version row with a new id — follow the drawer to it
        // so the open detail keeps pointing at the current head.
        if (id && saved && saved.id !== id) {
            setDetailId(saved.id);
        }
    };
    const detailEdit = detail ? {
        defaultValues: lcatDefaults(detail),
        onSubmit: (v)=>submitEntry(v, detail.id),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save as new version'
    } : undefined;
    const detailSections = detail ? [
        {
            heading: 'Labor category (as written)',
            rows: [
                {
                    label: 'Title',
                    value: detail.title,
                    editField: {
                        kind: 'text',
                        name: 'title',
                        label: 'Title (as written)',
                        required: true,
                        maxLength: 255
                    }
                },
                {
                    label: 'Company',
                    value: detail.companyName || '—',
                    editField: {
                        kind: 'select',
                        name: 'companyProfileId',
                        label: 'Company',
                        options: companyOptions
                    }
                },
                {
                    label: 'Vehicle',
                    value: detail.contractVehicleName || '—',
                    editField: {
                        kind: 'select',
                        name: 'contractVehicleId',
                        label: 'Vehicle',
                        options: vehicleOptions
                    }
                },
                {
                    label: 'Description',
                    value: detail.description || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'description',
                        label: 'Description',
                        rows: 3
                    }
                },
                {
                    label: 'Qualifications',
                    value: detail.qualifications || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'qualifications',
                        label: 'Qualifications',
                        rows: 3
                    }
                },
                {
                    label: 'Clearance',
                    value: detail.clearance || '—',
                    editField: {
                        kind: 'text',
                        name: 'clearance',
                        label: 'Clearance',
                        maxLength: 100
                    }
                },
                {
                    label: 'Certifications',
                    value: detail.certifications || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'certifications',
                        label: 'Certifications',
                        rows: 2
                    }
                },
                // Derived facts — read-only in edit mode too.
                {
                    label: 'Version',
                    value: `v${detail.version}`
                },
                {
                    label: 'Source',
                    value: detail.source === 'CANDIDATE' ? 'Extraction candidate' : 'Manual'
                }
            ]
        }
    ] : [];
    const candidateColumns = [
        {
            key: 'title',
            header: 'Title',
            render: (c)=>/*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex items-center gap-2",
                    children: [
                        c.title,
                        vehicleMismatch(c) && /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "warning",
                            "data-testid": Testids.deliveryRegistries.lcatCandidateMismatchBadge,
                            title: "Similar entries exist on a specific vehicle — accepting keeps this solicitation-scoped rather than minting it into that vehicle's catalog",
                            children: "Vehicle mismatch"
                        })
                    ]
                })
        },
        {
            key: 'companyName',
            header: 'Company',
            render: (c)=>c.companyName || '—'
        },
        {
            key: 'contractVehicleName',
            header: 'Vehicle',
            render: (c)=>c.contractVehicleName || '—'
        },
        {
            key: 'clearance',
            header: 'Clearance',
            render: (c)=>c.clearance || '—'
        },
        {
            key: 'candidateKey',
            header: 'From',
            // The feeder-owned key doubles as a provenance hint (zou:<project>:<code>).
            render: (c)=>c.zouProjectId || c.candidateKey
        }
    ];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-end justify-between gap-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraTabBar, {
                        idBase: "lcat-views",
                        className: "min-w-0 flex-1",
                        label: "Labor category views",
                        value: view,
                        onValueChange: setView,
                        options: [
                            {
                                value: 'catalog',
                                label: 'Catalog',
                                testId: Testids.deliveryRegistries.lcatViewCatalog
                            },
                            {
                                value: 'candidates',
                                label: 'Review queue',
                                count: candidates.length > 0 ? candidates.length : undefined,
                                testId: Testids.deliveryRegistries.lcatViewCandidates
                            },
                            {
                                value: 'mappings',
                                label: 'Mappings',
                                count: pendingMappings.length > 0 ? pendingMappings.length : undefined,
                                testId: Testids.deliveryRegistries.lcatViewMappings
                            },
                            {
                                value: 'compare',
                                label: 'Compare',
                                testId: Testids.deliveryRegistries.lcatViewCompare
                            }
                        ]
                    }),
                    view === 'catalog' && /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>setFormOpen(true),
                        "data-testid": Testids.deliveryRegistries.lcatCreate,
                        children: "New Labor Category"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraTabPanel, {
                active: view === 'catalog',
                idBase: "lcat-views",
                value: "catalog",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-center gap-4",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                "data-testid": Testids.deliveryRegistries.lcatSearchInput,
                                placeholder: "Search by title",
                                value: search,
                                onChange: (e)=>setSearch(e.target.value),
                                className: "max-w-sm"
                            }),
                            ownProfile && /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                className: "cursor-pointer items-center text-xs text-muted-foreground",
                                "data-testid": Testids.deliveryRegistries.lcatOwnCompanyToggle,
                                label: `${ownProfile.name} only`,
                                controlProps: {
                                    checked: oursOnly,
                                    onCheckedChange: (checked)=>setOursOnly(checked === true)
                                }
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(EntityTable, {
                        data: entries,
                        columns: entryColumns,
                        testid: Testids.deliveryRegistries.lcatTable,
                        rowTestid: Testids.deliveryRegistries.lcatRow,
                        rowKey: (e)=>e.id,
                        isLoading: entriesQuery.isLoading,
                        onRowClick: (row)=>{
                            setDetailMode('read');
                            setDetailId(row.id);
                        },
                        rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2 justify-end",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        onClick: ()=>{
                                            setDetailMode('edit');
                                            setDetailId(row.id);
                                        },
                                        children: "Edit"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        className: "text-danger hover:bg-danger-muted hover:text-danger",
                                        onClick: ()=>setDeleting(row),
                                        children: "Delete"
                                    })
                                ]
                            }),
                        emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                            title: "No labor categories yet",
                            description: "Create entries by hand, or accept extraction candidates from the Review queue — every company's LCATs as written, browsable by company and vehicle."
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraTabPanel, {
                active: view === 'candidates',
                idBase: "lcat-views",
                value: "candidates",
                children: /*#__PURE__*/ _jsx(EntityTable, {
                    data: candidates,
                    columns: candidateColumns,
                    testid: Testids.deliveryRegistries.lcatCandidatesTable,
                    rowTestid: Testids.deliveryRegistries.lcatCandidateRow,
                    rowKey: (c)=>c.id,
                    isLoading: candidatesQuery.isLoading,
                    rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                            className: "flex gap-2 justify-end",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    onClick: ()=>accept.mutate(row.id),
                                    disabled: accept.isPending || reject.isPending,
                                    "data-testid": Testids.deliveryRegistries.lcatCandidateAccept,
                                    children: "Accept"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "destructive",
                                    onClick: ()=>reject.mutate(row.id),
                                    disabled: accept.isPending || reject.isPending,
                                    "data-testid": Testids.deliveryRegistries.lcatCandidateReject,
                                    children: "Reject"
                                })
                            ]
                        }),
                    emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                        title: "Review queue is empty",
                        description: "Extraction pipelines file labor-category suggestions here with their evidence; accepting one adds it to the catalog."
                    })
                })
            }),
            /*#__PURE__*/ _jsx(SpecraTabPanel, {
                active: view === 'mappings',
                idBase: "lcat-views",
                value: "mappings",
                children: /*#__PURE__*/ _jsx(EntityTable, {
                    data: pendingMappings,
                    columns: [
                        {
                            key: 'fromEntryTitle',
                            header: 'Entry'
                        },
                        {
                            key: 'mappingType',
                            header: 'Relation',
                            render: (m)=>MAPPING_TYPE_LABELS[m.mappingType] ?? m.mappingType
                        },
                        {
                            key: 'target',
                            header: 'Target',
                            render: (m)=>mappingTarget(m)
                        },
                        {
                            key: 'confidence',
                            header: 'Confidence',
                            render: (m)=>m.confidence !== undefined ? `${Math.round(m.confidence * 100)}%` : '—'
                        },
                        {
                            key: 'evidence',
                            header: 'Evidence',
                            render: (m)=>{
                                const summary = evidenceSummary(m.evidenceJson);
                                return /*#__PURE__*/ _jsx("span", {
                                    className: "block max-w-72 truncate text-muted-foreground",
                                    title: summary === '—' ? undefined : m.evidenceJson,
                                    children: summary
                                });
                            }
                        }
                    ],
                    testid: Testids.deliveryRegistries.lcatMappingsTable,
                    rowTestid: Testids.deliveryRegistries.lcatMappingRow,
                    rowKey: (m)=>m.id,
                    isLoading: pendingMappingsQuery.isLoading,
                    rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                            className: "flex gap-2 justify-end",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    onClick: ()=>approveMapping.mutate(row.id),
                                    disabled: approveMapping.isPending || rejectMapping.isPending,
                                    "data-testid": Testids.deliveryRegistries.lcatMappingApprove,
                                    children: "Approve"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "destructive",
                                    onClick: ()=>rejectMapping.mutate(row.id),
                                    disabled: approveMapping.isPending || rejectMapping.isPending,
                                    "data-testid": Testids.deliveryRegistries.lcatMappingReject,
                                    children: "Reject"
                                })
                            ]
                        }),
                    emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                        title: "No pending mappings",
                        description: "Propose mappings from an entry's drawer — equivalences are reviewed here, never auto-approved, never transitive."
                    })
                })
            }),
            /*#__PURE__*/ _jsx(SpecraTabPanel, {
                active: view === 'compare',
                idBase: "lcat-views",
                value: "compare",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsxs(Select, {
                            value: compareAnchorId,
                            onValueChange: setCompareAnchorId,
                            children: [
                                /*#__PURE__*/ _jsx(SelectTrigger, {
                                    className: "max-w-md",
                                    "aria-label": "Occupational anchor",
                                    "data-testid": Testids.deliveryRegistries.lcatCompareAnchorSelect,
                                    children: /*#__PURE__*/ _jsx(SelectValue, {
                                        placeholder: "Pick an occupational anchor…"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SelectContent, {
                                    children: anchors.map((a)=>/*#__PURE__*/ _jsxs(SelectItem, {
                                            value: a.id,
                                            children: [
                                                a.socCode,
                                                " — ",
                                                a.title
                                            ]
                                        }, a.id))
                                })
                            ]
                        }),
                        compareAnchorId === '' ? /*#__PURE__*/ _jsx(EmptyState, {
                            title: "Side-by-side comparison",
                            description: "Pick a SOC anchor to see every company's approved-equivalent labor category next to each other — original text preserved, no fake normalization."
                        }) : compareMappings.length === 0 && !compareQuery.isLoading ? /*#__PURE__*/ _jsx(EmptyState, {
                            title: "No approved mappings on this anchor yet",
                            description: "Approve entry→anchor mappings in the Mappings queue and they line up here."
                        }) : /*#__PURE__*/ _jsx("div", {
                            className: "flex gap-4 overflow-x-auto pb-2",
                            children: compareMappings.map((m)=>{
                                const entry = allEntries.find((e)=>e.id === m.fromEntryId);
                                return /*#__PURE__*/ _jsxs("div", {
                                    className: "w-72 shrink-0 rounded-md border border-border p-3 text-[12.5px]",
                                    "data-testid": Testids.deliveryRegistries.lcatCompareColumn,
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                                            children: entry?.companyName || 'No company (as written)'
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "mt-1 font-medium",
                                            children: m.fromEntryTitle
                                        }),
                                        entry?.contractVehicleName && /*#__PURE__*/ _jsx("div", {
                                            className: "text-muted-foreground",
                                            children: entry.contractVehicleName
                                        }),
                                        entry?.clearance && /*#__PURE__*/ _jsxs("div", {
                                            className: "mt-1",
                                            children: [
                                                "Clearance: ",
                                                entry.clearance
                                            ]
                                        }),
                                        entry?.qualifications && /*#__PURE__*/ _jsx("p", {
                                            className: "mt-1 whitespace-pre-wrap text-muted-foreground",
                                            children: entry.qualifications
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "mt-2 space-y-1 border-t border-border/60 pt-2",
                                            children: [
                                                (ratesByEntry.get(m.fromEntryId) ?? []).map((r)=>/*#__PURE__*/ _jsxs("div", {
                                                        className: "flex items-center justify-between gap-2",
                                                        "data-testid": Testids.deliveryRegistries.lcatCompareRate,
                                                        children: [
                                                            /*#__PURE__*/ _jsxs("span", {
                                                                className: "text-muted-foreground",
                                                                children: [
                                                                    RATE_TYPE_LABELS[r.observationType] ?? r.observationType,
                                                                    r.periodLabel ? ` · ${r.periodLabel}` : ''
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsxs("span", {
                                                                className: "font-medium",
                                                                children: [
                                                                    "$",
                                                                    r.amount.toFixed(2)
                                                                ]
                                                            })
                                                        ]
                                                    }, r.id)),
                                                (ratesByEntry.get(m.fromEntryId) ?? []).length === 0 && /*#__PURE__*/ _jsx("div", {
                                                    className: "text-[11px] text-muted-foreground",
                                                    children: "No rates recorded yet"
                                                })
                                            ]
                                        })
                                    ]
                                }, m.id);
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Create Labor Category",
                fields: fields,
                defaultValues: lcatDefaults(null),
                onSubmit: async (v)=>{
                    await submitEntry(v, '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsxs(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: detail ? detail.title : '',
                sections: detailSections,
                testid: Testids.deliveryRegistries.lcatDetailDrawer,
                edit: detailEdit,
                canEdit: true,
                initialMode: detailMode,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.deliveryRegistries.lcatMappingsSection,
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                                children: "Mappings"
                            }),
                            entryMappings.map((m)=>/*#__PURE__*/ _jsx("div", {
                                    className: "rounded-md border border-border/60 p-2 text-[12.5px]",
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                                variant: m.status === 'APPROVED' ? 'success' : m.status === 'REJECTED' ? 'danger' : 'warning',
                                                children: m.status.toLowerCase()
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    MAPPING_TYPE_LABELS[m.mappingType] ?? m.mappingType,
                                                    " ",
                                                    mappingTarget(m)
                                                ]
                                            })
                                        ]
                                    })
                                }, m.id)),
                            entryMappings.length === 0 && !entryMappingsQuery.isLoading && /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: "No mappings yet — anchor this entry to a SOC occupation so it lines up across companies."
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: mapType,
                                        onValueChange: setMapType,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                className: "h-9 flex-1 min-w-36 text-xs",
                                                "aria-label": "Mapping relation",
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: Object.entries(MAPPING_TYPE_LABELS).map(([v, label])=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: v,
                                                        children: label
                                                    }, v))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: mapTargetKind,
                                        onValueChange: (v)=>setMapTargetKind(v),
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                className: "h-9 flex-1 min-w-36 text-xs",
                                                "aria-label": "Target kind",
                                                "data-testid": Testids.deliveryRegistries.lcatMapTargetKindSelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsxs(SelectContent, {
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectItem, {
                                                        value: "ANCHOR",
                                                        children: "SOC anchor"
                                                    }),
                                                    /*#__PURE__*/ _jsx(SelectItem, {
                                                        value: "ENTRY",
                                                        children: "Labor category"
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    mapTargetKind === 'ANCHOR' ? /*#__PURE__*/ _jsxs(Select, {
                                        value: mapAnchorId,
                                        onValueChange: setMapAnchorId,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                className: "h-9 flex-[2] min-w-52 text-xs",
                                                "aria-label": "Target anchor",
                                                "data-testid": Testids.deliveryRegistries.lcatMapToAnchorSelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {
                                                    placeholder: "Pick a SOC anchor…"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: anchors.map((a)=>/*#__PURE__*/ _jsxs(SelectItem, {
                                                        value: a.id,
                                                        children: [
                                                            a.socCode,
                                                            " — ",
                                                            a.title
                                                        ]
                                                    }, a.id))
                                            })
                                        ]
                                    }) : // The dedup move: assert this entry means the same thing as
                                    // another entry in the catalog (typically another vendor's
                                    // wording of the same job). Targets are current heads only,
                                    // never the entry itself.
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: mapEntryId,
                                        onValueChange: setMapEntryId,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                className: "h-9 flex-[2] min-w-52 text-xs",
                                                "aria-label": "Target labor category",
                                                "data-testid": Testids.deliveryRegistries.lcatMapToEntrySelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {
                                                    placeholder: "Pick a labor category…"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: allEntries.filter((e)=>e.id !== detail?.id).map((e)=>/*#__PURE__*/ _jsxs(SelectItem, {
                                                        value: e.id,
                                                        children: [
                                                            e.title,
                                                            e.companyName ? ` — ${e.companyName}` : ''
                                                        ]
                                                    }, e.id))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        disabled: (mapTargetKind === 'ANCHOR' ? !mapAnchorId : !mapEntryId) || propose.isPending || !detail,
                                        onClick: ()=>{
                                            if (!detail) return;
                                            propose.mutate({
                                                companyId: tenantId,
                                                fromEntryId: detail.id,
                                                toAnchorId: mapTargetKind === 'ANCHOR' ? mapAnchorId : undefined,
                                                toEntryId: mapTargetKind === 'ENTRY' ? mapEntryId : undefined,
                                                mappingType: mapType
                                            }, {
                                                onSuccess: ()=>{
                                                    setMapAnchorId('');
                                                    setMapEntryId('');
                                                }
                                            });
                                        },
                                        "data-testid": Testids.deliveryRegistries.lcatMapProposeButton,
                                        children: "Propose"
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.deliveryRegistries.lcatRatesSection,
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                                children: "Rates"
                            }),
                            orderedRates.map((r)=>{
                                const superseded = supersededRateIds.has(r.id);
                                return /*#__PURE__*/ _jsxs("div", {
                                    className: cn('rounded-md border border-border/60 p-2 text-[12.5px]', superseded && 'opacity-60'),
                                    "data-testid": Testids.deliveryRegistries.lcatRateRow,
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center justify-between gap-2",
                                            children: [
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: "flex items-center gap-2",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(SpecraBadge, {
                                                            variant: r.observationType === 'CEILING' ? 'neutral' : r.observationType === 'INFERRED' ? 'warning' : 'success',
                                                            children: RATE_TYPE_LABELS[r.observationType] ?? r.observationType
                                                        }),
                                                        /*#__PURE__*/ _jsxs("span", {
                                                            className: cn('font-medium', superseded && 'line-through'),
                                                            children: [
                                                                "$",
                                                                r.amount.toFixed(2),
                                                                /*#__PURE__*/ _jsxs("span", {
                                                                    className: "text-muted-foreground",
                                                                    children: [
                                                                        "/",
                                                                        r.rateUnit.toLowerCase().replace('hourly', 'hr')
                                                                    ]
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                }),
                                                !superseded && /*#__PURE__*/ _jsxs("span", {
                                                    className: "flex items-center gap-1",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                                            size: "sm",
                                                            variant: "ghost",
                                                            "data-testid": Testids.deliveryRegistries.lcatRateCorrect,
                                                            onClick: ()=>{
                                                                setCorrectingRateId(r.id);
                                                                setCorrectionAmount(r.amount.toFixed(2));
                                                            },
                                                            children: "Correct"
                                                        }),
                                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                                            size: "sm",
                                                            variant: "destructive",
                                                            onClick: ()=>deleteRate.mutate({
                                                                    companyId: tenantId,
                                                                    id: r.id
                                                                }),
                                                            children: "Remove"
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-muted-foreground",
                                            children: [
                                                r.periodLabel || 'no period stated',
                                                BURDEN_LABELS[r.burdenBasis] ?? r.burdenBasis,
                                                r.companyName,
                                                r.vehicleName
                                            ].filter(Boolean).join(' · ')
                                        }),
                                        superseded && /*#__PURE__*/ _jsx("div", {
                                            className: "text-[11px] text-muted-foreground",
                                            children: "Superseded by a correction — kept so the figure that was once believed stays on the record."
                                        }),
                                        r.correctsId && /*#__PURE__*/ _jsx("div", {
                                            className: "text-[11px] text-muted-foreground",
                                            children: "Corrects the struck figure above."
                                        }),
                                        correctingRateId === r.id && /*#__PURE__*/ _jsxs("div", {
                                            className: "mt-2 flex items-center gap-2",
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraInput, {
                                                    className: "h-8 w-24 text-xs",
                                                    "aria-label": "Corrected amount",
                                                    value: correctionAmount,
                                                    onChange: (e)=>setCorrectionAmount(e.target.value)
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "sm",
                                                    disabled: !correctionAmount || correctRate.isPending,
                                                    onClick: ()=>{
                                                        const amount = Number(correctionAmount);
                                                        if (Number.isNaN(amount)) return;
                                                        correctRate.mutate({
                                                            companyId: tenantId,
                                                            correctsId: r.id,
                                                            amount
                                                        }, {
                                                            onSuccess: ()=>{
                                                                setCorrectingRateId(null);
                                                                setCorrectionAmount('');
                                                            }
                                                        });
                                                    },
                                                    children: "Save correction"
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "sm",
                                                    variant: "ghost",
                                                    onClick: ()=>setCorrectingRateId(null),
                                                    children: "Cancel"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "text-[11px] text-muted-foreground",
                                                    children: "The original stays in the record."
                                                })
                                            ]
                                        })
                                    ]
                                }, r.id);
                            }),
                            drawerRates.length === 0 && !drawerRatesQuery.isLoading && /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: "No rates yet — record the posted ceiling and, when you learn it, what was actually agreed. The gap between those two is what the Rate Book is for."
                            }),
                            detail && /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: rateType,
                                        onValueChange: setRateType,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                className: "h-9 w-40 text-xs",
                                                "aria-label": "Rate type",
                                                "data-testid": Testids.deliveryRegistries.lcatRateTypeSelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: Object.entries(RATE_TYPE_LABELS).map(([v, label])=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: v,
                                                        children: label
                                                    }, v))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        className: "h-9 w-24 text-xs",
                                        "aria-label": "Amount",
                                        placeholder: "185.00",
                                        value: rateAmount,
                                        "data-testid": Testids.deliveryRegistries.lcatRateAmountInput,
                                        onChange: (e)=>setRateAmount(e.target.value)
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        className: "h-9 flex-1 min-w-32 text-xs",
                                        "aria-label": "Period label",
                                        placeholder: "Period as written (e.g. 2025/2026)",
                                        value: ratePeriod,
                                        "data-testid": Testids.deliveryRegistries.lcatRatePeriodInput,
                                        onChange: (e)=>setRatePeriod(e.target.value)
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: rateBurden,
                                        onValueChange: setRateBurden,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                className: "h-9 w-40 text-xs",
                                                "aria-label": "Burden basis",
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: Object.entries(BURDEN_LABELS).map(([v, label])=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: v,
                                                        children: label
                                                    }, v))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        disabled: !rateAmount || createRate.isPending,
                                        "data-testid": Testids.deliveryRegistries.lcatRateAdd,
                                        onClick: ()=>{
                                            if (!detail) return;
                                            const amount = Number(rateAmount);
                                            if (Number.isNaN(amount)) return;
                                            createRate.mutate({
                                                companyId: tenantId,
                                                lcatEntryId: detail.id,
                                                observationType: rateType,
                                                amount,
                                                burdenBasis: rateBurden,
                                                // The period is stored EXACTLY as typed; the comparison
                                                // layer is derived server-side, never in its place.
                                                periodLabel: ratePeriod,
                                                companyProfileId: detail.companyProfileId,
                                                contractVehicleId: detail.contractVehicleId
                                            }, {
                                                onSuccess: ()=>{
                                                    setRateAmount('');
                                                    setRatePeriod('');
                                                }
                                            });
                                        },
                                        children: "Record rate"
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.deliveryRegistries.lcatHistorySection,
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                                children: "History"
                            }),
                            history.map((v)=>/*#__PURE__*/ _jsxs("div", {
                                    className: "rounded-md border border-border/60 p-2 text-[12.5px]",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center gap-2",
                                            children: [
                                                /*#__PURE__*/ _jsxs(SpecraBadge, {
                                                    variant: v.id === detail?.id ? 'dark' : 'neutral',
                                                    children: [
                                                        "v",
                                                        v.version
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: "text-muted-foreground",
                                                    children: [
                                                        v.createdAt ? v.createdAt.toDate().toLocaleDateString() : '',
                                                        v.id === detail?.id ? ' · current' : ''
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "mt-1 font-medium",
                                            children: v.title
                                        }),
                                        v.qualifications && /*#__PURE__*/ _jsx("div", {
                                            className: "text-muted-foreground line-clamp-2",
                                            children: v.qualifications
                                        })
                                    ]
                                }, v.id)),
                            history.length === 0 && !historyQuery.isLoading && /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: "No history yet."
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Labor Category",
                description: deleting ? `Delete "${deleting.title}" (v${deleting.version})? Prior versions stay in history; references keep their version.` : undefined,
                onConfirm: async ()=>{
                    if (!deleting) return;
                    await remove.mutateAsync(deleting.id);
                    setDeleting(null);
                },
                isPending: remove.isPending
            })
        ]
    });
}
